import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
} from 'react';
import { Link } from 'react-router-dom';
import { articlePath } from '../help/articles';
import { getArticle } from '../help/registry';
import { getTip, type HelpTipId } from '../help/tips';
import { cn } from '../lib/utils';

export interface HelpTipProps {
  /** A registered tip id (help/tips.ts), e.g. `admin.rooms.create`. */
  id: HelpTipId;
  className?: string;
}

const POPOVER_MARGIN = 8;

/**
 * The small circled "?" that sits inline next to a label or page title. Click
 * or tap opens a popover with the tip's title, a few plain sentences and a
 * "How to: <article> →" link to the matching help article. Escape, a click
 * outside, or focus leaving it closes it. The popover is `fixed` (nothing around
 * the "?" moves, and a dialog body or scrolling table around it never clips it) and
 * kept inside the viewport, so it reads at phone width; the tap target is 40px
 * though the circle is 20px.
 *
 * Not a modal: focus stays on the "?" and Tab walks into the popover's link.
 * An unregistered id renders nothing (the typed `id` and the help tests catch it
 * before it ships; a stray one must never break a page).
 */
export function HelpTip({ id, className }: HelpTipProps) {
  const tip = getTip(id);
  const article = tip ? getArticle(tip.article) : undefined;
  const [open, setOpen] = useState(false);
  // Viewport coordinates of the popover (it is `fixed`, so a dialog body or a
  // scrolling table around the "?" never clips it).
  const [placement, setPlacement] = useState({ left: 0, top: 0 });
  const rootRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLSpanElement>(null);
  const popId = useId();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Centre the popover on the "?", then slide it back inside the viewport; flip it
  // above the "?" when there is no room below. Recomputed when the page scrolls or
  // resizes while it is open, so it stays attached to the "?".
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const trigger = triggerRef.current;
      const pop = popRef.current;
      if (!trigger || !pop) return;
      const t = trigger.getBoundingClientRect();
      const width = pop.offsetWidth;
      const height = pop.offsetHeight;
      const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
      const centred = t.left + t.width / 2 - width / 2;
      const left = Math.max(
        POPOVER_MARGIN,
        Math.min(centred, viewportWidth - width - POPOVER_MARGIN),
      );
      const above =
        t.bottom + height + POPOVER_MARGIN > window.innerHeight &&
        t.top - height - POPOVER_MARGIN > 0;
      const top = above ? t.top - height - POPOVER_MARGIN : t.bottom + POPOVER_MARGIN;
      setPlacement((prev) => (prev.left === left && prev.top === top ? prev : { left, top }));
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  if (!tip) return null;

  const close = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLSpanElement>) => {
    if (e.key !== 'Escape' || !open) return;
    // Handled here so a surrounding dialog does not also close.
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    close(true);
  };

  // Focus moving out of the "?" and its popover closes it. A click on the
  // popover's own text lands on the popover (tabIndex -1), so it stays open.
  const onBlur = (e: FocusEvent<HTMLSpanElement>) => {
    const next = e.relatedTarget as Node | null;
    if (!next || !rootRef.current?.contains(next)) setOpen(false);
  };

  return (
    <span
      ref={rootRef}
      className={cn('relative inline-flex align-middle', className)}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
    >
      <button
        ref={triggerRef}
        type="button"
        // Named just "Help", with the topic as its description: a name that repeats the
        // label beside it ("Help: Email") makes the field or button that label names
        // ambiguous to anything that finds controls by name.
        aria-label="Help"
        aria-description={tip.title}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popId : undefined}
        onClick={(e) => {
          // Inside a label or link, a click on the "?" must not activate it.
          e.preventDefault();
          setOpen((o) => !o);
        }}
        className={cn(
          'relative inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold leading-none transition-colors',
          'before:absolute before:-inset-[10px] before:content-[""]',
          open
            ? 'border-primary bg-primary-soft text-primary-ink'
            : 'border-line-strong bg-surface text-ink-2 hover:bg-surface-2 hover:text-ink',
        )}
      >
        <span aria-hidden="true">?</span>
      </button>
      {open ? (
        <span
          ref={popRef}
          id={popId}
          role="dialog"
          aria-label={tip.title}
          tabIndex={-1}
          style={{ left: placement.left, top: placement.top }}
          className="fixed z-40 block w-72 max-w-[calc(100vw-1rem)] whitespace-normal text-wrap rounded-lg border bg-surface p-3.5 text-left font-sans text-sm font-normal normal-case leading-5 tracking-normal text-ink shadow-md focus:outline-none"
        >
          <span className="block font-semibold">{tip.title}</span>
          <span className="mt-1 block text-ink-2">{tip.text}</span>
          {article ? (
            <Link
              to={articlePath(article.id, tip.anchor)}
              onClick={() => setOpen(false)}
              className="mt-2.5 inline-flex min-h-tap items-center text-sm font-medium text-primary-ink underline underline-offset-2 hover:text-primary-hover md:min-h-0"
            >
              {`How to: ${article.title} →`}
            </Link>
          ) : null}
        </span>
      ) : null}
    </span>
  );
}
